import React, { useMemo, useState } from "react";
import { Download, Loader2, Search } from "lucide-react";
import * as XLSX from "xlsx";
import Table, { buildExportRows } from "components/ui/Table";
import SearchSelect from "components/ui/SearchSelect";
import DatePicker from "components/financial/DatePicker";
import { useGetPaymentsQuery } from "apis/financial";
import { formatDate, formatCurrency } from "utils/helperFunctions";

/* -------------------------------------------------------------------------- */
/*  Payments — every charge triggered across Stripe and Campaign Flow         */
/* -------------------------------------------------------------------------- */

const formatNumber = (n) => new Intl.NumberFormat("en-AU").format(n || 0);
const money = (n) =>
  "$" +
  new Intl.NumberFormat("en-AU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n || 0);

const PERIODS = [
  { value: "", label: "All Time" },
  { value: "last-week", label: "Last 7 Days" },
  { value: "this-month", label: "This Month" },
  { value: "last-month", label: "Last Month" },
  { value: "last-6-months", label: "Last 6 Months" },
  { value: "last-year", label: "Last 12 Months" },
  { value: "custom", label: "Custom Range" },
];

const PLATFORMS = [
  { value: "", label: "All Platforms" },
  { value: "stripe", label: "Stripe" },
  { value: "campaignFlow", label: "CampaignFlow" },
  { value: "payOnSettlement", label: "Pay on Settlement" },
];

const STATUSES = [
  { value: "", label: "All Statuses" },
  { value: "completed", label: "Completed" },
  { value: "processing", label: "Processing" },
  { value: "pending", label: "Pending" },
  { value: "scheduled", label: "Scheduled" },
  { value: "failed", label: "Failed" },
  { value: "refundRequested", label: "Refund Requested" },
  { value: "refunded", label: "Refunded" },
  { value: "cancelled", label: "Cancelled" },
];

const PLATFORM_STYLE = {
  stripe: "bg-indigo-50 text-indigo-700",
  campaignFlow: "bg-teal-50 text-teal-700",
  payOnSettlement: "bg-amber-50 text-amber-700",
};
const PLATFORM_LABEL = {
  stripe: "Stripe",
  campaignFlow: "CampaignFlow",
  payOnSettlement: "Pay on Settlement",
};

const STATUS_STYLE = {
  completed: "bg-green-50 text-green-700",
  processing: "bg-blue-50 text-blue-700",
  pending: "bg-amber-50 text-amber-700",
  scheduled: "bg-sky-50 text-sky-700",
  failed: "bg-red-50 text-red-700",
  refundRequested: "bg-orange-50 text-orange-700",
  refunded: "bg-gray-100 text-darkergray",
  cancelled: "bg-gray-100 text-darkgray",
};
const STATUS_LABEL = {
  completed: "Completed",
  processing: "Processing",
  pending: "Pending",
  scheduled: "Scheduled",
  failed: "Failed",
  refundRequested: "Refund Requested",
  refunded: "Refunded",
  cancelled: "Cancelled",
};

const TYPE_DOT = {
  subscription: "bg-purple-500",
  transaction: "bg-blue-500",
  campaignAddOn: "bg-amber-500",
  campaignFunding: "bg-teal-500",
  fruitBox: "bg-lime-500",
  photoEdit: "bg-pink-500",
  photoCredits: "bg-rose-500",
  communityAlert: "bg-cyan-500",
  editorReview: "bg-indigo-500",
  priceProcess: "bg-violet-500",
  shop: "bg-orange-400",
  sms: "bg-green-500",
  marketing: "bg-yellow-500",
  other: "bg-gray-400",
};

const Pill = ({ className, children }) => (
  <span
    className={`inline-block px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${className}`}
  >
    {children}
  </span>
);

const StatCard = ({ label, value, sub }) => (
  <div className="bg-white rounded-lg border border-mediumgray p-4">
    <p className="text-sm text-darkgray mb-1">{label}</p>
    <p className="text-2xl font-bold text-black font-mono">{value}</p>
    {sub && <p className="text-xs text-darkgray mt-2">{sub}</p>}
  </div>
);

const columns = [
  {
    accessorKey: "chargedAt",
    header: "Charged Date",
    isSortable: true,
    minWidth: 130,
    cell: (row) => (
      <span className="font-mono text-sm whitespace-nowrap">
        {formatDate(row.chargedAt) || "—"}
      </span>
    ),
    exportValue: (row) => formatDate(row.chargedAt) || "",
  },
  {
    accessorKey: "platform",
    header: "Platform",
    isSortable: true,
    isFilter: true,
    minWidth: 120,
    cell: (row) => (
      <Pill className={PLATFORM_STYLE[row.platform]}>
        {PLATFORM_LABEL[row.platform] || row.platform}
      </Pill>
    ),
    exportValue: (row) => PLATFORM_LABEL[row.platform] || row.platform,
  },
  {
    accessorKey: "typeLabel",
    header: "Type",
    isSortable: true,
    isFilter: true,
    minWidth: 150,
    cell: (row) => (
      <span className="inline-flex items-center gap-2 whitespace-nowrap">
        <span
          className={`w-2 h-2 rounded-full ${TYPE_DOT[row.type] || TYPE_DOT.other}`}
        />
        {row.typeLabel}
      </span>
    ),
  },
  {
    accessorKey: "description",
    header: "Description",
    minWidth: 260,
    maxWidth: "480px",
    wrap: true,
  },
  {
    accessorKey: "agentName",
    header: "Agent",
    isSortable: true,
    minWidth: 160,
    cell: (row) => (
      <div>
        <p className="font-medium text-black">{row.agentName}</p>
        {row.companyName && (
          <p className="text-xs text-darkgray truncate max-w-[220px]">
            {row.companyName}
          </p>
        )}
      </div>
    ),
  },
  // Export-only (the Agent cell already shows the company in the grid).
  { accessorKey: "companyName", header: "Company" },
  {
    accessorKey: "amount",
    header: "Amount",
    isSortable: true,
    minWidth: 110,
    cell: (row) => (
      <span className="font-mono font-semibold text-black whitespace-nowrap">
        {money(row.amount)}
      </span>
    ),
    exportValue: (row) => row.amount,
  },
  {
    accessorKey: "status",
    header: "Status",
    isSortable: true,
    isFilter: true,
    minWidth: 120,
    cell: (row) => (
      <Pill className={STATUS_STYLE[row.status] || STATUS_STYLE.cancelled}>
        {STATUS_LABEL[row.status] || row.status}
      </Pill>
    ),
    exportValue: (row) => STATUS_LABEL[row.status] || row.status,
  },
  {
    accessorKey: "reference",
    header: "Reference",
    minWidth: 160,
    cell: (row) =>
      row.receiptUrl ? (
        <a
          href={row.receiptUrl}
          target="_blank"
          rel="noreferrer"
          className="text-xs font-mono text-blue-600 hover:underline"
        >
          {row.reference || "Receipt"}
        </a>
      ) : (
        <span className="text-xs font-mono text-darkgray">
          {row.reference || "—"}
        </span>
      ),
  },
];

// Company is exported but hidden in the grid (agent cell already shows it).
const visibleColumns = columns.filter((c) => c.accessorKey !== "companyName");

const PaymentExpanded = ({ row }) => (
  <div className="bg-lightgray/40 px-6 py-4 text-sm grid grid-cols-1 md:grid-cols-3 gap-3">
    {row.address && (
      <div>
        <p className="text-xs text-darkgray">Property</p>
        <p className="text-black">{row.address}</p>
      </div>
    )}
    {row.agentEmail && (
      <div>
        <p className="text-xs text-darkgray">Agent email</p>
        <p className="text-black">{row.agentEmail}</p>
      </div>
    )}
    {row.scheduledFor && (
      <div>
        <p className="text-xs text-darkgray">Scheduled for</p>
        <p className="text-black">{formatDate(row.scheduledFor)}</p>
      </div>
    )}
    {row.paymentMethod && (
      <div>
        <p className="text-xs text-darkgray">Payment method</p>
        <p className="text-black">{row.paymentMethod}</p>
      </div>
    )}
    {row.surcharge > 0 && (
      <div>
        <p className="text-xs text-darkgray">Surcharge</p>
        <p className="text-black">{money(row.surcharge)}</p>
      </div>
    )}
    {row.agentContribution > 0 && (
      <div>
        <p className="text-xs text-darkgray">Agent contribution</p>
        <p className="text-black">{money(row.agentContribution)}</p>
      </div>
    )}
    {row.refundAmount != null && (
      <div>
        <p className="text-xs text-darkgray">Refund amount</p>
        <p className="text-black">{money(row.refundAmount)}</p>
      </div>
    )}
    {row.failureReason && (
      <div className="md:col-span-3">
        <p className="text-xs text-darkgray">Failure reason</p>
        <p className="text-red-700">{row.failureReason}</p>
      </div>
    )}
    {row.lineItems?.length > 0 && (
      <div className="md:col-span-3">
        <p className="text-xs text-darkgray mb-1">
          Line items ({row.lineItems.length})
        </p>
        <ul className="space-y-0.5">
          {row.lineItems.map((li, i) => (
            <li key={i} className="flex justify-between gap-4">
              <span className="text-black">
                {li.description}
                {li.agentNames?.length ? ` · ${li.agentNames.join(", ")}` : ""}
              </span>
              <span className="font-mono">{money(li.amount)}</span>
            </li>
          ))}
        </ul>
      </div>
    )}
    {row.agentNames?.length > 0 && !row.lineItems?.length && (
      <div className="md:col-span-3">
        <p className="text-xs text-darkgray">Agents</p>
        <p className="text-black">{row.agentNames.join(", ")}</p>
      </div>
    )}
  </div>
);

const hasDetail = (row) =>
  !!(
    row.address ||
    row.agentEmail ||
    row.scheduledFor ||
    row.paymentMethod ||
    row.surcharge > 0 ||
    row.agentContribution > 0 ||
    row.refundAmount != null ||
    row.failureReason ||
    row.lineItems?.length
  );

const EMPTY_FILTERS = {
  period: "",
  startDate: "",
  endDate: "",
  platform: "",
  type: "",
  status: "",
};

const Payments = () => {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [search, setSearch] = useState("");

  const { data, isPending, isFetching } = useGetPaymentsQuery({
    period: filters.period === "custom" ? "custom" : filters.period,
    startDate: filters.period === "custom" ? filters.startDate : "",
    endDate: filters.period === "custom" ? filters.endDate : "",
    platform: filters.platform,
    type: filters.type,
    status: filters.status,
  });

  const summary = data?.summary || {};
  const types = data?.types || [];

  // Search is client-side so it feels instant on an already-loaded ledger.
  const rows = useMemo(() => {
    const payments = data?.payments || [];
    const q = search.trim().toLowerCase();
    if (!q) return payments;
    return payments.filter((r) =>
      `${r.agentName} ${r.agentEmail} ${r.companyName} ${r.description} ${r.address} ${r.reference}`
        .toLowerCase()
        .includes(q),
    );
  }, [data, search]);

  const typeOptions = [
    { value: "", label: "All Types" },
    ...types.map((t) => ({ value: t.key, label: t.label })),
  ];
  const pick = (opts, v) => opts.find((o) => o.value === v) || opts[0];
  const set = (key) => (opt) =>
    setFilters((prev) => ({ ...prev, [key]: opt ? opt.value : "" }));

  const exportRows = () => buildExportRows(columns, rows);
  const exportXlsx = () => {
    const ws = XLSX.utils.json_to_sheet(exportRows());
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Payments");
    XLSX.writeFile(wb, "payments.xlsx");
  };
  const exportCsv = () => {
    const ws = XLSX.utils.json_to_sheet(exportRows());
    const csv = XLSX.utils.sheet_to_csv(ws);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "payments.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const byStatus = summary.byStatus || {};
  const isLoading = isPending || isFetching;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h5 className="font-semibold text-black tracking-wide uppercase">
            Payments
          </h5>
          <p className="text-sm text-darkgray">
            All triggered payments across Stripe and CampaignFlow
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={exportCsv}
            disabled={!rows.length}
            className="flex items-center gap-2 px-3 py-2 border border-mediumgray bg-white rounded-md text-sm text-darkergray hover:bg-lightgray hover:text-black transition-colors disabled:opacity-50"
          >
            <Download size={14} /> CSV
          </button>
          <button
            type="button"
            onClick={exportXlsx}
            disabled={!rows.length}
            className="flex items-center gap-2 px-3 py-2 border border-mediumgray bg-white rounded-md text-sm text-darkergray hover:bg-lightgray hover:text-black transition-colors disabled:opacity-50"
          >
            <Download size={14} /> XLSX
          </button>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
        <StatCard
          label="Total Payments"
          value={money(summary.total?.amount)}
          sub={`${formatNumber(summary.total?.count)} records`}
        />
        <StatCard
          label="Stripe"
          value={money(summary.stripe?.amount)}
          sub={`${formatNumber(summary.stripe?.count)} payments`}
        />
        <StatCard
          label="CampaignFlow"
          value={money(summary.campaignFlow?.amount)}
          sub={`${formatNumber(summary.campaignFlow?.count)} payments`}
        />
        <StatCard
          label="Pay on Settlement"
          value={money(summary.payOnSettlement?.amount)}
          sub={`${formatNumber(summary.payOnSettlement?.count)} owed at settlement`}
        />
        <StatCard
          label="Completed"
          value={formatNumber(byStatus.completed?.count)}
          sub="payments"
        />
        <StatCard
          label="Processing"
          value={formatNumber(
            (byStatus.processing?.count || 0) + (byStatus.pending?.count || 0),
          )}
          sub={
            byStatus.failed?.count
              ? `${formatNumber(byStatus.failed.count)} failed · ${formatCurrency(byStatus.failed.amount)}`
              : "payments"
          }
        />
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-mediumgray p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <SearchSelect
            options={PERIODS}
            value={pick(PERIODS, filters.period)}
            onChange={set("period")}
            searchable={false}
          />
          <SearchSelect
            options={PLATFORMS}
            value={pick(PLATFORMS, filters.platform)}
            onChange={set("platform")}
            searchable={false}
          />
          <SearchSelect
            options={typeOptions}
            value={pick(typeOptions, filters.type)}
            onChange={set("type")}
          />
          <SearchSelect
            options={STATUSES}
            value={pick(STATUSES, filters.status)}
            onChange={set("status")}
            searchable={false}
          />
          <div className="relative">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-darkgray"
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search agent, description..."
              className="w-full pl-9 pr-3 py-2 border border-mediumgray rounded-md text-sm focus:outline-none focus:border-black"
            />
          </div>
        </div>
        {filters.period === "custom" && (
          <div className="mt-4 max-w-sm">
            <label className="form-label">Custom range</label>
            <DatePicker
              value={{ startDate: filters.startDate, endDate: filters.endDate }}
              onChange={({ startDate, endDate }) =>
                setFilters((prev) => ({ ...prev, startDate, endDate }))
              }
            />
          </div>
        )}
      </div>

      {/* Ledger */}
      <div className="relative bg-white rounded-lg border border-mediumgray p-4">
        {isLoading && (
          <div className="absolute inset-0 bg-white/60 flex items-center justify-center z-10 rounded-lg">
            <Loader2 className="w-8 h-8 text-black animate-spin" />
          </div>
        )}
        <Table
          columns={visibleColumns}
          data={rows}
          pageSize={25}
          pageSizeOptions={[25, 50, 100, 250]}
          searchable={false}
          emptyText="No payments found"
          renderExpanded={(row) => <PaymentExpanded row={row} />}
          isRowExpandable={hasDetail}
        />
      </div>
    </div>
  );
};

export default Payments;
